import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { normalizeName } from '../submeterCsv';
export const normalizeMeterId = value => (value || '').trim().toLowerCase();
const compareNatural = (a, b) => a.localeCompare(b, undefined, {
  numeric: true,
  sensitivity: 'base'
});
const compareByLocation = (a, b) => {
  if (!a.location !== !b.location) return a.location ? -1 : 1;
  return compareNatural(a.location, b.location) || compareNatural(a.id, b.id);
};
const collect = (groups, key, id, location) => {
  if (!groups.has(key)) groups.set(key, {
    id,
    locations: new Map(),
    hasBlankLocation: false
  });
  const group = groups.get(key);
  if (!location) {
    group.hasBlankLocation = true;
    return;
  }
  const locationKey = location.toLowerCase();
  if (!group.locations.has(locationKey)) group.locations.set(locationKey, location);
};
const locationText = group => [...group.locations.values()].join(', ');
const columnIndex = (headers, column) => column && headers.filter(h => h === column).length === 1 ? headers.indexOf(column) : -1;
export const sortByLocation = items => [...items].sort(compareByLocation);
export const readSheet = (rows, headers, meterColumn, locationColumn) => {
  const meterIdx = columnIndex(headers, meterColumn);
  if (meterIdx === -1) return {
    meters: [],
    options: []
  };
  const locationIdx = columnIndex(headers, locationColumn);
  const byKey = new Map();
  const byValue = new Map();
  rows.forEach(row => {
    const id = row[meterIdx];
    if (!id) return;
    const location = locationIdx === -1 ? '' : row[locationIdx] || '';
    collect(byKey, normalizeMeterId(id), id, location);
    collect(byValue, id, id, location);
  });
  const meters = [...byKey].map(_ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      group = _ref2[1];
    return {
      key,
      id: group.id,
      location: locationText(group),
      sameLocation: !group.hasBlankLocation && group.locations.size === 1 ? locationText(group) : ''
    };
  });
  const options = [...byValue].map(_ref3 => {
    let _ref4 = _slicedToArray(_ref3, 2),
      value = _ref4[0],
      group = _ref4[1];
    const location = locationText(group);
    return {
      value,
      id: value,
      location,
      label: location ? "".concat(value, " (").concat(location, ")") : value
    };
  });
  return {
    meters: meters.sort(compareByLocation),
    options: options.sort(compareByLocation)
  };
};
const readOwn = (record, key) => Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
const withMeter = (ids, meterId) => {
  const next = [...ids];
  const blank = next.findIndex(id => !id.trim());
  if (blank === -1) next.push(meterId);else next[blank] = meterId;
  return next;
};
export const moveMeter = (unitMeterIds, meter, targetUnitId) => {
  const target = targetUnitId == null ? null : String(targetUnitId);
  const next = {};
  Object.entries(unitMeterIds).forEach(_ref5 => {
    let _ref6 = _slicedToArray(_ref5, 2),
      unitId = _ref6[0],
      ids = _ref6[1];
    if (unitId === target) {
      next[unitId] = ids;
      return;
    }
    const kept = ids.filter(id => normalizeMeterId(id) !== meter.key);
    if (kept.length === ids.length) next[unitId] = ids;else next[unitId] = kept.length ? kept : [''];
  });
  if (target !== null) {
    const ids = next[target] || [''];
    next[target] = ids.some(id => normalizeMeterId(id) === meter.key) ? ids : withMeter(ids, meter.id);
  }
  return next;
};
export const findHolders = (unitList, unitMeterIds) => {
  const holders = new Map();
  unitList.forEach(unit => {
    new Set((unitMeterIds[unit.id] || []).map(normalizeMeterId).filter(Boolean)).forEach(key => {
      holders.set(key, [...(holders.get(key) || []), unit]);
    });
  });
  return holders;
};
const readTemplateUnits = (unitList, unitMappings) => {
  const byName = new Map();
  if (!unitMappings) return byName;
  unitList.forEach(unit => {
    var _readOwn;
    const mapped = (_readOwn = readOwn(unitMappings, unit.label)) !== null && _readOwn !== void 0 ? _readOwn : readOwn(unitMappings, unit.id);
    if (typeof mapped !== 'string' || !mapped.trim()) return;
    const key = normalizeName(mapped);
    byName.set(key, [...(byName.get(key) || []), unit]);
  });
  return byName;
};
export const findEmptyUnitIds = (unitList, unitMeterIds) => new Set(unitList.filter(unit => !(unitMeterIds[unit.id] || []).some(id => id.trim())).map(unit => String(unit.id)));

// What a pre-fill pass added: unit id -> normalised meter IDs that were on the unit after and not before.
export const findPlaced = (before, after) => {
  const placed = {};
  Object.entries(after).forEach(_ref7 => {
    let _ref8 = _slicedToArray(_ref7, 2),
      unitId = _ref8[0],
      ids = _ref8[1];
    const had = new Set((before[unitId] || []).map(normalizeMeterId));
    const added = [...new Set(ids.map(normalizeMeterId).filter(key => key && !had.has(key)))];
    if (added.length) placed[unitId] = added;
  });
  return placed;
};

// Takes back only the placements given, from the units they sit on. Anything else on a unit stays.
export const clearPlaced = (unitMeterIds, placed) => Object.fromEntries(Object.entries(unitMeterIds).map(_ref9 => {
  let _ref0 = _slicedToArray(_ref9, 2),
    unitId = _ref0[0],
    ids = _ref0[1];
  const taken = placed[unitId];
  if (!taken) return [unitId, ids];
  const kept = ids.filter(id => !taken.includes(normalizeMeterId(id)));
  return [unitId, kept.length ? kept : ['']];
}));
export const forgetUnitPlacements = (placed, unitId) => Object.fromEntries(Object.entries(placed).filter(_ref1 => {
  let _ref10 = _slicedToArray(_ref1, 1),
    placedUnitId = _ref10[0];
  return placedUnitId !== String(unitId);
}));
export const prefillMeterIds = _ref11 => {
  let meters = _ref11.meters,
    unitList = _ref11.unitList,
    unitMeterIds = _ref11.unitMeterIds,
    unitMappings = _ref11.unitMappings,
    eligibleUnitIds = _ref11.eligibleUnitIds;
  const templateUnits = readTemplateUnits(unitList, unitMappings);
  const resolveUnits = location => {
    var _templateUnits$get;
    return (_templateUnits$get = templateUnits.get(normalizeName(location))) !== null && _templateUnits$get !== void 0 ? _templateUnits$get : unitList.filter(unit => normalizeName(unit.label) === normalizeName(location));
  };
  const emptyUnitIds = eligibleUnitIds !== null && eligibleUnitIds !== void 0 ? eligibleUnitIds : findEmptyUnitIds(unitList, unitMeterIds);
  const held = new Set(Object.values(unitMeterIds).flat().map(normalizeMeterId).filter(Boolean));
  return meters.reduce((filled, meter) => {
    if (held.has(meter.key) || !meter.sameLocation) return filled;
    const resolved = resolveUnits(meter.sameLocation);
    if (resolved.length !== 1 || !emptyUnitIds.has(String(resolved[0].id))) return filled;
    return _objectSpread(_objectSpread({}, filled), {}, {
      [resolved[0].id]: withMeter(filled[resolved[0].id] || [''], meter.id)
    });
  }, unitMeterIds);
};